/**
 * Utility functions for handling API errors and preventing object rendering in React
 */

/**
 * Safely extract error message from axios error response
 * Prevents rendering validation error objects in React components
 * 
 * @param {Error} error - Axios error object
 * @param {string} fallbackMessage - Default message if no valid string error found
 * @returns {string} Safe error message string
 */
export const getErrorMessage = (error, fallbackMessage = 'An error occurred') => {
  try {
    // Check if there's a detail field and it's a string
    if (error.response?.data?.detail && typeof error.response.data.detail === 'string') {
      return error.response.data.detail;
    }
    
    // Check if there's a message field and it's a string
    if (error.response?.data?.message && typeof error.response.data.message === 'string') {
      return error.response.data.message;
    }
    
    // Check if error has a message property
    if (error.message && typeof error.message === 'string') {
      return error.message;
    }
    
    // If none of the above, return fallback
    return fallbackMessage;
  } catch (e) {
    // If anything goes wrong, return fallback
    return fallbackMessage;
  }
};

/**
 * Safely render any value in React JSX
 * Converts objects to strings to prevent React rendering errors
 * 
 * @param {any} value - Value to render
 * @returns {string|number|null} Safe value for React rendering
 */
export const safeRender = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  
  if (typeof value === 'object') {
    // Don't render objects directly - convert to string representation
    try {
      return JSON.stringify(value);
    } catch (e) {
      return '[Object]';
    }
  }
  
  return String(value);
};

/**
 * Check if a value is safe to render in React
 * 
 * @param {any} value - Value to check
 * @returns {boolean} True if safe to render
 */
export const isSafeToRender = (value) => {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    Array.isArray(value)
  );
};